import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const LandingPage = () => {
  const features = [
    {
      icon: "🏗️",
      title: "Gestion Centralisée",
      description:
        "Gérez tous vos chantiers depuis une interface unique et intuitive",
      gradient: "from-blue-500 to-blue-600",
    },
    {
      icon: "💰",
      title: "Suivi des Charges",
      description:
        "Contrôlez vos coûts avec un système de suivi détaillé des charges",
      gradient: "from-green-500 to-green-600",
    },
    {
      icon: "📊",
      title: "Tableau de Bord",
      description:
        "Visualisez vos données avec des graphiques et des statistiques en temps réel",
      gradient: "from-purple-500 to-purple-600",
    },
    {
      icon: "🔄",
      title: "Mise à Jour Temps Réel",
      description: "Synchronisation automatique de toutes vos données",
      gradient: "from-orange-500 to-orange-600",
    },
    {
      icon: "📱",
      title: "Responsive Design",
      description: "Accédez à votre plateforme depuis n'importe quel appareil",
      gradient: "from-pink-500 to-pink-600",
    },
    {
      icon: "🔒",
      title: "Sécurisé",
      description:
        "Vos données sont protégées avec les meilleurs standards de sécurité",
      gradient: "from-red-500 to-red-600",
    },
  ];

  // State for chiffre d'affaire
  const [chiffreAffaire, setChiffreAffaire] = useState(null);
  const [loadingCA, setLoadingCA] = useState(true);
  const [stats, setStats] = useState({
    totalChantiers: 0,
    chantiersActifs: 0,
    totalCharges: 0,
  });

  useEffect(() => {
    // Fetch all chantiers and compute chiffre d'affaire as sum of budgets (prixPrestation) for "en cours"
    async function fetchChiffreAffaire() {
      setLoadingCA(true);
      try {
        // Fetch all chantiers
        const chantiersRes = await fetch(`${API_URL}/api/chantiers`);
        const chantiers = await chantiersRes.json();

        // Filter only "en cours"
        const enCoursChantiers = Array.isArray(chantiers)
          ? chantiers.filter((c) => c.etat === "en cours")
          : [];

        // Update stats
        setStats({
          totalChantiers: Array.isArray(chantiers) ? chantiers.length : 0,
          chantiersActifs: enCoursChantiers.length,
          totalCharges: 0, // not used for CA here
        });

        // CA = sum of prixPrestation for all "en cours" chantiers
        const totalBudget = enCoursChantiers.reduce(
          (sum, chantier) => sum + (Number(chantier.prixPrestation) || 0),
          0
        );

        setChiffreAffaire(totalBudget);
      } catch (e) {
        setChiffreAffaire(null);
      } finally {
        setLoadingCA(false);
      }
    }
    fetchChiffreAffaire();
  }, []);

  return (
    <div className="modern-landing">
      {/* Hero Section */}
      <section className="hero-modern">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <div className="hero-container">
          <div className="hero-content-modern">
            <div className="hero-badge">
              <span className="badge-icon">✨</span>
              <span>Plateforme de gestion moderne</span>
            </div>

            <h1 className="hero-title-modern">
              Gérez vos{" "}
              <span className="gradient-text">Chantiers</span>
              <br />
              avec <span className="gradient-text">MOBIL PLUS</span>
            </h1>

            <p className="hero-subtitle-modern">
              Une solution complète et moderne pour optimiser la gestion de vos
              projets de construction. Suivez vos chantiers, contrôlez vos charges
              et maximisez votre rentabilité.
            </p>

            <div className="hero-actions-modern">
              <Link to="/chantiers" className="btn-modern btn-primary-modern">
                <span>🚀</span>
                Commencer maintenant
              </Link>
              <Link to="/add" className="btn-modern btn-secondary-modern">
                <span>+</span>
                Nouveau chantier
              </Link>
            </div>
          </div>

          <div className="hero-visual-modern">
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.totalChantiers}</div>
                  <div className="stat-label">Chantiers total</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">⚡</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.chantiersActifs}</div>
                  <div className="stat-label">Chantiers actifs</div>
                </div>
              </div>

            </div>

            {/* Main CA Card */}
            <div className="ca-card-modern">
              <div className="ca-header">
                <div className="ca-icon">💰</div>
                <div className="ca-indicator">
                  <div className="indicator-dot"></div>
                  <span>En temps réel</span>
                </div>
              </div>

              <div className="ca-content">
                <div className="ca-label">Chiffre d'Affaire</div>
                <div className="ca-amount">
                  {loadingCA ? (
                    <div className="loading-skeleton">
                      <div className="skeleton-line"></div>
                    </div>
                  ) : (
                    <span className="amount-value">
                      {chiffreAffaire !== null
                        ? chiffreAffaire.toLocaleString("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                            maximumFractionDigits: 0,
                          })
                        : "0 €"}
                    </span>
                  )}
                </div>
              
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-modern">
        <div className="features-container">
          <div className="features-header">
            <h2 className="features-title">
              Pourquoi choisir{" "}
              <span className="gradient-text">MOBIL PLUS</span> ?
            </h2>
            <p className="features-subtitle">
              Des outils puissants et intuitifs pour transformer votre gestion de
              chantiers
            </p>
          </div>

          <div className="features-grid-modern">
            {features.map((feature, index) => (
              <div key={index} className="feature-card-modern">
                <div className="feature-icon-container">
                  <div className="feature-icon-modern">{feature.icon}</div>
                </div>
                <h3 className="feature-title-modern">{feature.title}</h3>
                <p className="feature-description-modern">
                  {feature.description}
                </p>
                <div className="feature-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
